import type { ReactNode } from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Two panes side by side, with a divider you can drag.
 *
 * **The width is a CSS custom property, not a React style.** Styling is Tailwind
 * classes here and `noInlineStyles` fails the build on a `style` attribute — so
 * the fraction is written to `--split` on the frame, the way `Popover` writes
 * the position it works out. The grid reads it with a fallback, which is also
 * what the first paint uses: `var(--split, 50%)` needs nothing set to be
 * correct before the first drag.
 *
 * **Dragging is pointer events with capture**, so a pointer that leaves the
 * divider — or the window — keeps dragging until it is released. Mouse-move on
 * the document would do the same thing and also keep firing after the button
 * came up somewhere this element never heard about.
 *
 * **It works from a keyboard.** The divider is a `separator` that takes focus
 * and answers the arrow keys, because a control only a pointer can reach is one
 * a good half of people cannot use. It reports where it is, so a screen reader
 * says "62%" rather than nothing.
 *
 * **One column below `lg`.** Two panes side by side on a phone are two panes
 * too narrow to read, and a divider dragged with a thumb on a 4px target is a
 * control nobody hits. Stacked, there is nothing to divide, so the handle goes.
 */

/** Neither pane may be squeezed to nothing: below this it is not a pane. */
const NARROWEST = 0.2
const WIDEST = 0.8
/** What an arrow key moves. Coarse enough to get somewhere, fine enough to aim. */
const STEP = 0.02

export function SplitPane({
  start,
  end,
  label,
}: {
  readonly start: ReactNode
  readonly end: ReactNode
  /** What the divider is between, for whoever cannot see it. */
  readonly label: string
}) {
  const frame = useRef<HTMLDivElement>(null)
  const [fraction, setFraction] = useState(0.5)

  useEffect(() => {
    frame.current?.style.setProperty('--split', `${Math.round(fraction * 1000) / 10}%`)
  }, [fraction])

  const move = useCallback((clientX: number) => {
    const box = frame.current?.getBoundingClientRect()
    if (box === undefined || box.width === 0) return
    const next = (clientX - box.left) / box.width
    setFraction(Math.min(Math.max(next, NARROWEST), WIDEST))
  }, [])

  const nudge = useCallback((by: number) => {
    setFraction((current) => Math.min(Math.max(current + by, NARROWEST), WIDEST))
  }, [])

  return (
    <div
      className="grid min-h-0 flex-1 grid-rows-[1fr_auto_1fr] lg:grid-cols-[var(--split,50%)_auto_1fr] lg:grid-rows-1"
      ref={frame}
    >
      <div className="flex min-h-0 min-w-0 flex-col">{start}</div>

      {/*
        An `hr`, which is what a separator is — and one that takes focus and
        carries a value is the splitter widget, so no `role` is needed to say
        so. The element is 12px of hit area with a 1px line: `bg-clip-content`
        paints the background inside the borders only, so the transparent ones
        either side are target without being visible. A 1px line is what this
        should look like and a miserable thing to aim at.
      */}
      <hr
        aria-label={label}
        aria-orientation="vertical"
        aria-valuemax={Math.round(WIDEST * 100)}
        aria-valuemin={Math.round(NARROWEST * 100)}
        aria-valuenow={Math.round(fraction * 100)}
        className="hidden h-full touch-none select-none border-transparent border-y-0 bg-line bg-clip-content transition-colors hover:bg-brand focus-visible:bg-brand lg:block lg:w-3 lg:cursor-col-resize lg:border-x-[5.5px]"
        onKeyDown={(event) => {
          if (event.key === 'ArrowLeft') nudge(-STEP)
          else if (event.key === 'ArrowRight') nudge(STEP)
          else if (event.key === 'Home') setFraction(NARROWEST)
          else if (event.key === 'End') setFraction(WIDEST)
          else return
          event.preventDefault()
        }}
        onPointerDown={(event) => {
          event.currentTarget.setPointerCapture(event.pointerId)
          move(event.clientX)
        }}
        onPointerMove={(event) => {
          if (event.currentTarget.hasPointerCapture(event.pointerId)) move(event.clientX)
        }}
        tabIndex={0}
      />

      <div className="flex min-h-0 min-w-0 flex-col">{end}</div>
    </div>
  )
}
